import { useInfiniteQuery } from 'react-query';

import { CursorCollectionResponse } from 'types/api_response';
import {
  infiniteEmptyResults,
  baseApiUrl,
  fetcher,
  getToken,
  ServerErrorResponse,
  createParamString,
} from 'data_sources/constants';
import { Notification } from '../_types';

export default function useNotifications<T extends Notification>(
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/notifications/v1/admin`;
  const idToken = getToken();

  return useInfiniteQuery<CursorCollectionResponse<T>, ServerErrorResponse>(
    ['notifications', idToken, params],
    async ({ pageParam, signal }): Promise<CursorCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: CursorCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: 1000 * 60 * 5, // 5 minutes
      getNextPageParam: (lastPage) => {
        if (lastPage.lastKey && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            lastKey: lastPage.lastKey,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
